import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const URGENCY_ACCENT = { routine: "#4ADE80", urgent: "#FBBF24", stat: "#F87171" };

/**
 * ReferralModal — doctor-to-doctor handoff from inside the Encounter Workspace.
 *
 * Presents:
 *  - Target specialty picker (list from /platform/directory)
 *  - Optional named-doctor within that specialty (auto-loaded from directory)
 *  - Urgency chips
 *  - Referral reason (short) + full clinical summary
 *  - Auto-snapshots the encounter's diagnoses + orders as context
 *
 * On save → POSTs /api/referrals and closes.
 */
export default function ReferralModal({ encounter, patient, onClose, onSaved }) {
  const [directory, setDirectory] = useState({ departments: [], doctors_by_department: {} });
  const [toSpecialty, setToSpecialty] = useState("");
  const [toDoctorId, setToDoctorId] = useState("");
  const [urgency, setUrgency] = useState("routine");
  const [reason, setReason] = useState("");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/directory");
        setDirectory(data);
      } catch { /* soft */ }
    })();
  }, []);

  // Pre-fill summary with encounter context
  useEffect(() => {
    if (!encounter) return;
    const parts = [];
    if (encounter.chief_complaint) parts.push(`Presenting: ${encounter.chief_complaint}`);
    if (encounter.assessment) parts.push(`Assessment: ${encounter.assessment}`);
    if (encounter.plan) parts.push(`Plan so far: ${encounter.plan}`);
    if ((encounter.diagnoses || []).length) {
      parts.push(`Coded diagnoses: ${encounter.diagnoses.map((d) => `${d.code} ${d.title}`).join(", ")}`);
    }
    setSummary(parts.join("\n\n"));
  }, [encounter]);

  const availableDoctors = useMemo(() => {
    if (!toSpecialty) return [];
    return directory.doctors_by_department?.[toSpecialty] || [];
  }, [toSpecialty, directory]);

  async function save() {
    if (!toSpecialty) { toast.error("Pick a target specialty"); return; }
    if (!reason.trim()) { toast.error("Add a short referral reason"); return; }
    if (!summary.trim()) { toast.error("Clinical summary is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/referrals", {
        patient_id: patient?.id || encounter.patient_id,
        encounter_id: encounter?.id,
        to_specialty: toSpecialty,
        to_doctor_id: toDoctorId || null,
        urgency,
        reason: reason.trim(),
        clinical_summary: summary.trim(),
        relevant_diagnoses: (encounter?.diagnoses || []).map((d) => ({ code: d.code, title: d.title })),
        relevant_orders: (encounter?.order_ids || []),
      });
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send referral");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose} data-testid="ref-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xl">Refer to another specialty</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              The receiving doctor will see this in their inbox.
            </div>
          </div>
          <button onClick={onClose} data-testid="ref-close"
                  className="text-[#A3ACA7] hover:text-white text-xl leading-none">✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Target specialty</span>
            <select value={toSpecialty} onChange={(e) => { setToSpecialty(e.target.value); setToDoctorId(""); }}
                    data-testid="ref-specialty" className="ros-input">
              <option value="">— pick —</option>
              {(directory.departments || []).map((d) => (
                <option key={d.key} value={d.key}>{d.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Specific doctor (optional)</span>
            <select value={toDoctorId} onChange={(e) => setToDoctorId(e.target.value)}
                    disabled={!toSpecialty || availableDoctors.length === 0}
                    data-testid="ref-doctor" className="ros-input disabled:opacity-40">
              <option value="">— any doctor in this specialty —</option>
              {availableDoctors.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Urgency</span>
          {["routine", "urgent", "stat"].map((u) => (
            <button key={u} onClick={() => setUrgency(u)} type="button"
                    data-testid={`ref-urgency-${u}`}
                    className={`text-xs px-3 py-1 rounded-full font-bold ${
                      urgency === u ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                    }`}
                    style={urgency === u ? { background: URGENCY_ACCENT[u] } : {}}>
              {u.toUpperCase()}
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Reason for referral (short)
          </span>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 data-testid="ref-reason" className="ros-input"
                 placeholder="e.g. r/o secondary hypertension, please stress-test" />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Clinical summary (pre-filled from encounter)
          </span>
          <textarea rows={7} value={summary} onChange={(e) => setSummary(e.target.value)}
                    data-testid="ref-summary" className="ros-input"
                    placeholder="Full narrative for the receiving doctor" />
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#272E2B]">
          <button onClick={onClose} data-testid="ref-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="ref-send"
                  className="flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[#38BDF8] text-black font-semibold disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send referral
          </button>
        </div>
      </div>
    </div>
  );
}
